import { useState } from "react";
import "./style.css";
import { Breadcrumb, PopUpModal } from "../../Components/Atoms";
import { CSVUploader } from "../../Components/Molecules";
import PATHS from "../../Routes/paths";
import { Button, Spinner, useToast } from "@chakra-ui/react";
import { UploadingIcon } from "../../Assets/images/svgs";

import { useNavigate, useLocation } from "react-router-dom";
import { submitRenewalRequest } from "../../APIs";

const REQUIRED_HEADERS = [
  "Dealership",
  "Registration Number",
  "Device IMEI",
  "Chassis Number"
];

export default function UploadRenewalList() {
  const [uploadedRows, setUploadedRows] = useState([]);
  const [popupModalData, setPopupModalData] = useState(null);
  const Toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const dealers = location.state?.dealers || [];

  const breadcrumbPaths = [
    { idx: 1, name: "Subscription Summary", link: PATHS.DEVICE_SUBSCRIPTION },
    { idx: 2, name: "Upload Device IMEI List", isCurrentPage: true },
  ];

  const handleDataProcessed = (rows, invalidRows) => {
    // Replace empty fields with hyphens for display, but keep original for validation if needed
    // Actually, CSVUploader handles display. We just need to validate.
    setUploadedRows(rows);
  };

  const validateRow = (row) => {
    let isValid = true;
    const fieldErrors = {};

    // Helper to check if value is empty or just hyphen
    const isEmpty = (val) => !val || val.toString().trim() === "" || val.toString().trim() === "-";

    // Validate Dealership
    const dealerName = row["Dealership"];
    const dealer = dealers.find(d => d.label === dealerName);
    if (isEmpty(dealerName) || !dealer) {
      isValid = false;
      fieldErrors["Dealership"] = true;
    }

    // Validate IMEI (15 digits, numeric)
    const imei = row["Device IMEI"];
    if (isEmpty(imei) || !/^\d{15}$/.test(imei)) {
      isValid = false;
      fieldErrors["Device IMEI"] = true;
    }

    // Validate Registration Number
    if (isEmpty(row["Registration Number"])) {
      isValid = false;
      fieldErrors["Registration Number"] = true;
    }

    // Validate Chassis Number
    if (isEmpty(row["Chassis Number"])) {
      isValid = false;
      fieldErrors["Chassis Number"] = true;
    }

    return { isValid, errors: fieldErrors };
  };

  const handleRenewalSuccess = () => {
    navigate(`${PATHS.DEVICE_SUBSCRIPTION}?tab=2`);
  };

  const handleRenewalRequest = () => {
    setPopupModalData({
      open: true,
      modalMsg: <Spinner className="renewal-request-loader" size="lg" />,
    });

    const payload = {
      devices: uploadedRows.map((row) => {
        const dealer = dealers.find(d => d.label === row["Dealership"]);
        return {
          dlrId: dealer ? dealer.value : null,
          regNo: row["Registration Number"],
          imei: row["Device IMEI"],
          chassisNo: row["Chassis Number"]
        };
      }),
    };

    submitRenewalRequest(payload)
      .then((res) => {
        setPopupModalData({
          open: true,
          modalMsg: (
            <div className="renewal-success-icon">
              <UploadingIcon />
              <p>Renewal request submitted for {uploadedRows.length} devices</p>
            </div>
          ),
          confirmBtn: "Go to Renewal History",
          onConfirm: handleRenewalSuccess,
          onModalClose: handleRenewalSuccess,
        });

        Toast({
          title: "Success",
          description: `Renewal request submitted for ${uploadedRows.length} devices`,
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "bottom",
        });
      })
      .catch((err) => {
        const errorMessage = err || "Error submitting renewal request. Please try again.";
        setPopupModalData({
          open: true,
          modalMsg: errorMessage,
          closeBtn: "Close",
          onModalClose: () => setPopupModalData(null),
        });
      });
  };

  const confirmRenewalRequest = () => {
    setPopupModalData({
      open: true,
      modalMsg: `You are requesting renewal for ${uploadedRows.length} devices. Are you sure you want to continue?`,
      closeBtn: "Cancel",
      confirmBtn: "Continue",
      onConfirm: handleRenewalRequest,
      onModalClose: () => setPopupModalData(null),
    });
  };

  return (
    <section className="panel-main upload-renewal-list">
      <Breadcrumb paths={breadcrumbPaths} />
      <div className="upload-renewal-container">
        <p className="operation-field-label">Upload Device IMEI List</p>

        <CSVUploader
          requiredHeaders={REQUIRED_HEADERS}
          onDataProcessed={handleDataProcessed}
          maxRows={100}
          templateName="DeviceIMEITemplate.csv"
          customValidation={validateRow}
        />

        {uploadedRows.length > 0 && (
          <Button className="request-renewal-btn" onClick={confirmRenewalRequest}>
            Request Renewal
          </Button>
        )}
      </div>

      <PopUpModal {...popupModalData} />
    </section>
  );
}
